import type { PlaybackState } from '../../ports/audio';
import type { MediaSessionHandlers, MediaSessionPort } from '../../ports/media-session';

/**
 * Edino mesto v aplikaciji, kjer se sme pojaviti `navigator.mediaSession`.
 *
 * Lint pravilo tega ne ujame — `mediaSession` ni global, ampak lastnost —
 * zato mejo čuva `tests/unit/audio-boundary.test.ts`.
 */

/**
 * Dejanja, ki jih izrecno **ne** ponudimo (D1, faza 1).
 *
 * Živ tok nima trajanja. Če jih ne postavimo na `null`, jih iOS na
 * zaklenjenem zaslonu ponudi kot delujoče — in gumb, ki ne more delovati,
 * je slabši od gumba, ki ga ni.
 */
const DISABLED_ACTIONS = ['seekto', 'seekbackward', 'seekforward'] as const;

function supported(): boolean {
  return typeof navigator !== 'undefined' && 'mediaSession' in navigator;
}

function trySetHandler(
  action: MediaSessionAction,
  handler: MediaSessionActionHandler | null,
): void {
  try {
    navigator.mediaSession.setActionHandler(action, handler);
  } catch {
    // Brskalnik dejanja ne pozna. Ostala ostanejo nastavljena.
  }
}

export function createMediaSession(): MediaSessionPort | null {
  if (!supported()) return null;

  const registered: MediaSessionAction[] = [];

  function register(action: MediaSessionAction, handler: () => void): void {
    trySetHandler(action, handler);
    registered.push(action);
  }

  return {
    setHandlers(handlers: MediaSessionHandlers) {
      /**
       * Prijava se ponovi ob vsakem prehodu v `playing` (iOS jo rabi — glej
       * `playback.ts`). Ponovni `setActionHandler` z istim rokovalnikom je v
       * Blinku no-op, a `registered` je pred tem raslo za 5 ob vsakem prehodu.
       * Počistimo ga tu, PRED ponovno prijavo — rokovalnikov ne postavljamo na
       * `null`, zato ni vrzeli (⛔ HANDOFF: null-pa-znova prijava spremeni nabor).
       */
      registered.length = 0;

      register('play', () => {
        handlers.onPlay();
      });
      register('pause', () => {
        handlers.onPause();
      });
      register('stop', () => {
        handlers.onStop();
      });
      register('previoustrack', () => {
        handlers.onPrevious();
      });
      register('nexttrack', () => {
        handlers.onNext();
      });

      for (const action of DISABLED_ACTIONS) trySetHandler(action, null);
    },

    setNowPlaying(info) {
      if (info === null) {
        navigator.mediaSession.metadata = null;
        return;
      }

      navigator.mediaSession.metadata = new MediaMetadata({
        title: info.title,
        ...(info.artist === undefined ? {} : { artist: info.artist }),
        ...(info.album === undefined ? {} : { album: info.album }),
        ...(info.artworkUrl === undefined ? {} : { artwork: [{ src: info.artworkUrl }] }),
      });
    },

    setPlaybackState(state: PlaybackState) {
      // `loading` in `reconnecting` sta za sistem še vedno »igra« — sicer
      // zaklenjen zaslon ob vsakem medpomnjenju preskoči na gumb za predvajanje.
      navigator.mediaSession.playbackState =
        state === 'idle' || state === 'error' ? 'paused' : 'playing';
    },

    detach() {
      for (const action of registered) trySetHandler(action, null);
      registered.length = 0;
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
    },
  };
}
